/**
 * StopwatchControls - Start/Stop Timer mit Live-Anzeige
 *
 * Zeigt:
 * - Laufende Zeit seit Arbeitsbeginn (aktualisiert jede Sekunde)
 * - SwipeButton zum Unterbrechen der Arbeit
 * - Anzahl der laufenden Timer
 */

import { useEffect } from 'preact/hooks';
import { signal, computed } from '@preact/signals';
import { DateTime } from 'luxon';
import { Users, MapPin } from 'lucide-preact';
import {
  hasRunningEntries,
  workdayStartTime,
  presentCount,
  stopDay,
  isLoading,
  isCapturingLocation,
} from '../../stores/timesheet.store';
import { SwipeButton } from '../../components/ui/SwipeButton';
import styles from './StopwatchControls.module.css';

// Lokaler Signal fuer Live-Timer (aktualisiert jede Sekunde)
const elapsedTime = signal('00:00');

interface StopwatchControlsProps {
  onStopped?: () => void;
}

export function StopwatchControls({ onStopped }: StopwatchControlsProps) {
  // Live-Timer Update
  useEffect(() => {
    if (!hasRunningEntries.value) {
      elapsedTime.value = '00:00';
      return;
    }

    function updateElapsed() {
      const startIso = workdayStartTime.value;
      if (!startIso) {
        elapsedTime.value = '00:00';
        return;
      }

      const start = DateTime.fromISO(startIso);
      const now = DateTime.now();
      const diff = now.diff(start, ['hours', 'minutes', 'seconds']);

      const hours = Math.floor(diff.hours);
      const minutes = Math.floor(diff.minutes);

      // Format: HH:MM
      const formatted = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      elapsedTime.value = formatted;
    }

    // Sofort aktualisieren
    updateElapsed();

    // Jede Sekunde aktualisieren
    const interval = setInterval(updateElapsed, 1000);

    return () => clearInterval(interval);
  }, [hasRunningEntries.value, workdayStartTime.value]);

  // Start-Zeit formatieren
  const formattedStartTime = computed(() => {
    const startIso = workdayStartTime.value;
    if (!startIso) return '';
    const start = DateTime.fromISO(startIso);
    return start.toFormat('HH:mm');
  });

  // Stop Handler
  async function handleStop() {
    const success = await stopDay();
    if (success && onStopped) {
      onStopped();
    }
  }

  // Render: Timer laeuft
  if (hasRunningEntries.value) {
    return (
      <div className={styles.container}>
        <div className={styles.timerDisplay}>
          <span className={styles.timerLabel}>Arbeitszeit</span>
          <span className={styles.timerValue}>{elapsedTime.value}</span>
          <span className={styles.timerSubtext}>
            Gestartet um {formattedStartTime.value} Uhr
          </span>
        </div>

        <div className={styles.infoSection}>
          <div className={styles.infoRow}>
            <span className={styles.pulseIndicator} />
            <Users size={16} className={styles.infoIcon} />
            <span>{presentCount.value} Mitarbeiter aktiv</span>
          </div>
        </div>

        <SwipeButton
          label="Arbeit unterbrechen / Stopp"
          loadingLabel="Stoppe Timer..."
          onConfirm={handleStop}
          disabled={isLoading.value}
          loading={isCapturingLocation.value}
          variant="danger"
        />

        {isCapturingLocation.value && (
          <div className={`${styles.gpsIndicator} ${styles.capturing}`}>
            <MapPin size={14} />
            <span>GPS wird ermittelt...</span>
          </div>
        )}
      </div>
    );
  }

  // Kein Timer aktiv - sollte nicht erreicht werden (TimesheetPage zeigt dann IdleControls)
  return null;
}
